// components/capture/RecordingRegion.jsx — Anamoria SPA
// v2.1 — NEW-130 copy fix (2026-09-28): the mid-replace status line shipped in
//   v2.0 ("Recording a new version — see below") was never in the copy doc —
//   added directly in code during the build. Sophie flagged it against the
//   live deployed UI: it used an em dash (brand-voice rule: never use em
//   dashes) and read as passive, not as an instruction. Fixed per
//   new130-copy-DRAFT-for-review-v0.4.md §5 — see VOICE_REPLACING_NOTE /
//   VIDEO_REPLACING_NOTE below. No other behavior changed.
//
// v2.0 — NEW-130 (2026-09-28): split from one Voice|Video toggle region into a
//   single-kind region — the parent now renders TWO <RecordingRegion>s
//   (kind="voice", kind="video") in place of the old merged one, the same way
//   Write / A photo already stand apart. Approved via new130-moment-voice-
//   video-split-plan-DRAFT-for-review-v0.3.md + new130-copy-DRAFT-for-review-
//   v0.3.md + new130-embedded-vs-underneath-mockup-v0.2.html (Option 1).
//
//   What changed:
//   - NEW required prop `kind`. The old internal Voice|Video `.segmented`
//     toggle is gone; `activeRec` is now just `kind === 'voice' ? voice :
//     video` (both recorder hooks are still instantiated unconditionally —
//     same as before — only the one matching `kind` is ever driven).
//   - Existing recordings of this kind now render as a REAL preview per
//     piece — the same `VoiceCard`/`VideoCard` used everywhere else in the
//     app renders a memory, in `embedded` mode, via a small adapter
//     (`toVoiceCardMemory`/`toVideoCardMemory`) instead of the old generic
//     waveform-icon `.recChip`. A region can hold more than one piece of its
//     kind (unchanged capability — nothing here ever capped that; see
//     AddMemoryPage.jsx's own `addRecording`/`removeRecording`).
//   - NEW "Re-record" action, shown only on a piece that already has a
//     `serverId` (i.e. existed on the server before this edit session — a
//     freshly-added local piece is simpler to just Remove and re-add, so it
//     never gets a Re-record affordance; this also means create mode, where
//     nothing ever has a serverId, never shows it — matches the plan's own
//     scope for §3E). Tapping it shows an inline confirm — NOT the
//     RecordPage.jsx/VideoPage.jsx modal (that pattern is asymmetric between
//     voice/video today and was deliberately not ported, copy v0.3 §4) —
//     using the region's own existing Remove-confirm visual pattern:
//       "This recording will be replaced. Record again?" [Record again][Keep it]
//     Confirming sets `replacingId` and defers to the same idle record/
//     library UI already used for adding a piece; when that capture
//     finishes, the old piece is removed and the new one added together
//     (`onRemoveRecording` + `onAddRecording`, same tick).
//   - `atCap` no longer blocks the idle capture UI while a Re-record is in
//     progress (`replacingId` set) — a replace doesn't grow the piece count,
//     so it must still be reachable at the 10-piece cap (this is exactly
//     what makes a stuck sole piece un-stuck; see plan §3C).
//   - `hideRemove` (A-6 sole-piece rule) still hides Remove only. Re-record
//     stays available even when `hideRemove` is true — that's the point.
//   - Dropped: the shared `audioElRef`/`togglePlay`/`playingId`/`PauseGlyph`
//     "one audio element at a time" chip-playback code. Each `VoiceCard`
//     manages its own playback now, same as everywhere else `VoiceCard`
//     already renders (feed, MomentCard, MomentDetailPage's own read view at
//     line 389) — none of those coordinate a single shared player either, so
//     this isn't a new gap, just this region matching how the rest of the
//     app has already worked. Flagging it plainly rather than silently.
//
// v1.1 — 2.2b: AD card reframe (July 29, 2026). [prior history unchanged below]
// v1.0 — C1 extraction from AddMemoryPage v0.3 (July 22, 2026)
//
// Props:
//   kind             — 'voice' | 'video' — NEW, required. Fixed per instance.
//   recordings       — this kind's own pieces only — [{ id, serverId?, kind,
//                       duration, durationFormatted, url, blob?, mimeType?,
//                       serverPiece? }]. Parent filters by kind before passing.
//   onAddRecording   — function({ kind, blob, mimeType, duration, durationFormatted })
//   onRemoveRecording — function(id)
//   atCap            — boolean — TOTAL piece count across the whole moment
//   saving           — boolean (disables record + library buttons)
//   hideRemove       — boolean — A-6 sole-piece rule (Remove only; Re-record
//                       is exempt, see above)
//   getApi           — function — passed straight through to VoiceCard
//                       (signed-URL fallback; unused since playbackUrl is
//                       always supplied, but kept for parity/future-proofing)
//   theme            — string — passed straight through to VoiceCard (default 'warm')
//   className        — string — page entrance-animation class

import { useState, useEffect, useRef } from 'react';
import { useRecorder } from '../../hooks/useRecorder';
import { useVideoRecorder } from '../../hooks/useVideoRecorder';
import styles from '../../pages/AddMemoryPage.module.css';
import RegionCard, { RecordingIcon } from './RegionCard';
import VoiceCard from '../VoiceCard';
import VideoCard from '../VideoCard';

// Closed copy, new130-copy-DRAFT-for-review-v0.3.md §2 — mirrors PhotoRegion's
// real PHOTO_HELPER pattern exactly.
const VOICE_HELPER = 'A voice note, if you have one.';
const VIDEO_HELPER = 'A video, if you have one.';
const VOICE_TITLE = 'A voice note';
const VIDEO_TITLE = 'A video';

// Closed copy, new130-copy-DRAFT-for-review-v0.4.md §5 — shown in place of the
// Re-record/Remove row while a Re-record capture is pending (replacingId set).
const VOICE_REPLACING_NOTE = 'Record or choose a new voice note below to replace this one.';
const VIDEO_REPLACING_NOTE = 'Record or choose a new video below to replace this one.';

// VideoPage v1.4 library-upload parity (re-homed; Sophie 2026-07-19): a video
// piece can be recorded OR picked from the library, matching the deployed
// VideoPage. Validation + error strings re-homed verbatim. Audio library
// upload added same session (voicemail case) — see block below.
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.webm,.mov';
// Audio library upload (Sophie 2026-07-19: "what if it is a voicemail from the
// lost loved one?") — formats per AD §1.9 (MP3/WAV/M4A/OGG; M4A = iPhone
// voicemail exports). FLAG carried from v0.1: the 5-min duration cap is
// inherited from the recording cap — revisit for uploads (see v0.3 header trail).
const ACCEPTED_AUDIO_EXTENSIONS = '.mp3,.wav,.m4a,.ogg';
const ACCEPTED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/x-m4a', 'audio/ogg'];
const MAX_AUDIO_FILE_SIZE = 50 * 1024 * 1024;   // AD §1.9
const MAX_AUDIO_DURATION_SEC = 300;             // see FLAG above
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024;
const MAX_VIDEO_DURATION_SEC = 300;

function formatSec(sec) {
  const m = Math.floor(sec / 60);
  const ss = Math.round(sec % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
}

/** Validate a library-picked audio file (mirrors validateVideoFile). */
function validateAudioFile(file) {
  return new Promise((resolve) => {
    if (!ACCEPTED_AUDIO_TYPES.includes(file.type)) {
      resolve({ valid: false, error: 'We can’t add this kind of file. MP3, WAV, M4A, or OGG recordings will work.' });
      return;
    }
    if (file.size > MAX_AUDIO_FILE_SIZE) {
      resolve({ valid: false, error: 'We weren’t able to add this recording because the file is very large. A version under 50 MB will work.' });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement('audio');
    a.preload = 'metadata';
    a.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (a.duration > MAX_AUDIO_DURATION_SEC) {
        resolve({ valid: false, error: 'This recording runs longer than the 5 minutes we can hold right now. Nothing has happened to your recording. Your copy is safe where it is.' });
      } else {
        resolve({ valid: true, error: null, duration: Math.round(a.duration) });
      }
    };
    a.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: 'We can’t add this kind of file. MP3, WAV, M4A, or OGG recordings will work.' });
    };
    a.src = url;
  });
}

/** Validate a library-picked video (VideoPage v1.4 pattern). */
function validateVideoFile(file) {
  return new Promise((resolve) => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      resolve({ valid: false, error: 'We can’t add this kind of file. MP4, WebM, or MOV videos will work.' });
      return;
    }
    if (file.size > MAX_VIDEO_FILE_SIZE) {
      resolve({ valid: false, error: 'We weren’t able to add this video because the file is very large. A version under 100 MB will work.' });
      return;
    }
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (v.duration > MAX_VIDEO_DURATION_SEC) {
        resolve({ valid: false, error: 'This video runs longer than the 5 minutes we can hold right now. Nothing has happened to your video. Your copy is safe where it is.' });
      } else {
        resolve({ valid: true, error: null, duration: Math.round(v.duration) });
      }
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: 'We can’t add this kind of file. MP4, WebM, or MOV videos will work.' });
    };
    v.src = url;
  });
}

// ─── NEW-130: adapters — normalize a local-or-saved recording entry into the
// minimal `memory`-shaped object VoiceCard/VideoCard already expect. Neither
// component reads anything else while `embedded` (title/isPrivate/isFavorite/
// createdAt are all gated behind `!embedded` in both components — verified
// against the live files before writing this). `r.url` is already a valid
// playable source either way: a local blob URL for an unsaved recording, or
// the real signed URL for a saved one. `r.serverPiece` (set by
// MomentDetailPage's handleEnterEdit — see that file's own NEW-130 note) is
// the raw server piece, so a saved recording gets its real waveform/poster;
// a local one falls back to the same decorative bars / no-poster behavior
// every other unsaved recording already has, everywhere in the app. ───
function toVoiceCardMemory(r) {
  return {
    id: r.id,
    voiceNote: {
      duration: r.duration,
      playbackUrl: r.url,
      waveform: r.serverPiece?.voiceNote?.waveform ?? null,
    },
  };
}

function toVideoCardMemory(r) {
  return {
    id: r.id,
    videoNote: {
      durationSeconds: r.duration,
      playbackUrl: r.url,
      posterUrl: r.serverPiece?.videoNote?.posterUrl ?? null,
    },
  };
}

export default function RecordingRegion({
  kind,
  recordings,
  onAddRecording,
  onRemoveRecording,
  atCap,
  saving,
  hideRemove = false,
  getApi,
  theme = 'warm',
  className = '',
}) {
  const [recExpanded, setRecExpanded] = useState(false); // 2.2b: card expand (collapsed entry → controls)
  const [confirmRemoveId, setConfirmRemoveId] = useState(null); // existing Remove confirm (unchanged)
  const [confirmReRecordId, setConfirmReRecordId] = useState(null); // NEW-130: Re-record confirm
  const [replacingId, setReplacingId] = useState(null); // NEW-130: which piece the next capture replaces
  const [libraryError, setLibraryError] = useState('');
  const videoFileInputRef = useRef(null);   // library picker (VideoPage v1.4 parity)
  const audioFileInputRef = useRef(null);   // audio library picker (voicemails etc.)
  const videoPreviewRef = useRef(null);

  const voice = useRecorder();
  const video = useVideoRecorder();

  // Both hooks are always instantiated (unchanged from v1.x) — only the one
  // matching `kind` is ever driven, since `kind` is fixed per instance now.
  const recBusy =
    voice.recordingState === 'recording' || voice.recordingState === 'paused' ||
    video.recordingState === 'recording' || video.recordingState === 'paused';
  const activeRec = kind === 'voice' ? voice : video;

  const title = kind === 'voice' ? VOICE_TITLE : VIDEO_TITLE;
  const helper = kind === 'voice' ? VOICE_HELPER : VIDEO_HELPER;

  // 2.2b: the card is open when there are recordings, the recorder is busy, or
  // the person expanded it — and it stays open through a recording session.
  const open = recExpanded || recordings.length > 0 || recBusy;

  const finishCapture = (data) => {
    if (replacingId != null) {
      // NEW-130: atomic-in-state swap — old piece out, new piece in, same tick.
      onRemoveRecording(replacingId);
      onAddRecording(data);
      setReplacingId(null);
    } else {
      onAddRecording(data);
    }
  };

  // ─── Collect finished voice recording (R8) ───
  useEffect(() => {
    if (kind === 'voice' && voice.recordingState === 'stopped' && voice.audioBlob) {
      const blob = voice.audioBlob;
      finishCapture({
        kind: 'voice',
        blob,
        mimeType: voice.mimeType,
        duration: voice.duration,
        durationFormatted: voice.durationFormatted,
      });
      voice.reRecord(); // reset hook to idle for the next piece
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, voice.recordingState, voice.audioBlob]);

  // ─── Collect finished video recording (R8) ───
  useEffect(() => {
    if (kind === 'video' && video.recordingState === 'stopped' && video.videoBlob) {
      const blob = video.videoBlob;
      finishCapture({
        kind: 'video',
        blob,
        mimeType: video.mimeType,
        duration: video.duration,
        durationFormatted: video.durationFormatted,
      });
      video.reRecord();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, video.recordingState, video.videoBlob]);

  // ─── Live camera preview while recording video (BPR §1.10; R2 body cue) ───
  useEffect(() => {
    if (videoPreviewRef.current && video.previewStream) {
      videoPreviewRef.current.srcObject = video.previewStream;
    }
  }, [video.previewStream]);

  const handleAudioFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || (atCap && replacingId == null)) return;
    setLibraryError('');
    const result = await validateAudioFile(file);
    if (!result.valid) { setLibraryError(result.error); return; }
    finishCapture({
      kind: 'voice',
      blob: file,
      mimeType: file.type,
      duration: result.duration,
      durationFormatted: formatSec(result.duration),
    });
  };

  const handleVideoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || (atCap && replacingId == null)) return;
    setLibraryError('');
    const result = await validateVideoFile(file);
    if (!result.valid) { setLibraryError(result.error); return; }
    finishCapture({
      kind: 'video',
      blob: file,
      mimeType: file.type,
      duration: result.duration,
      durationFormatted: formatSec(result.duration),
    });
  };

  const handleRemove = (id) => {
    setConfirmRemoveId(null);
    onRemoveRecording(id);
  };

  // NEW-130: Re-record — inline confirm, closed copy v0.3 §4 (not the
  // RecordPage/VideoPage modal; mirrors this region's own Remove-confirm).
  const handleStartReRecord = (id) => {
    setConfirmRemoveId(null);
    setConfirmReRecordId(id);
  };
  const handleConfirmReRecord = (id) => {
    setConfirmReRecordId(null);
    setReplacingId(id);
  };
  const handleCancelReRecord = () => setConfirmReRecordId(null);

  // ─── Permission-denied copy (copy v0.2 §4) replaces hook error strings ───
  const permissionLine = (err) =>
    err && /denied/i.test(err)
      ? 'Recording needs your permission. You can turn it on in your browser settings whenever you’re ready'
      : err;

  return (
    <fieldset className={`${styles.region} ${className}`}>
      <legend className={styles.hiddenLabel}>{title}</legend>
      <RegionCard
        icon={<RecordingIcon />}
        title={title}
        helper={helper}
        open={open}
        disabled={atCap && recordings.length === 0}
        onToggle={() => setRecExpanded(true)}
      >

      {/* Existing recordings of this kind — one real preview card each (NEW-130) */}
      {recordings.length > 0 && (
        <div className={styles.recCardsCol}>
          {recordings.map((r) => {
            const canReRecord = !!r.serverId; // only a piece that pre-dates this edit session
            const beingReplaced = replacingId === r.id;
            return (
              <div key={r.id} className={styles.recCardWrap}>
                {kind === 'voice' ? (
                  <VoiceCard memory={toVoiceCardMemory(r)} getApi={getApi} theme={theme} embedded />
                ) : (
                  <VideoCard memory={toVideoCardMemory(r)} embedded />
                )}

                {beingReplaced ? (
                  <p className={styles.replacingNote}>
                    {kind === 'voice' ? VOICE_REPLACING_NOTE : VIDEO_REPLACING_NOTE}
                  </p>
                ) : confirmRemoveId === r.id ? (
                  <span className={styles.removeConfirm} role="alert">
                    This recording won&rsquo;t be kept. Remove it?
                    <button type="button" className={styles.removeLink} onClick={() => handleRemove(r.id)}>Remove</button>
                    <button type="button" className={styles.keepLink} onClick={() => setConfirmRemoveId(null)}>Keep it</button>
                  </span>
                ) : confirmReRecordId === r.id ? (
                  <span className={styles.removeConfirm} role="alert">
                    This recording will be replaced. Record again?
                    <button type="button" className={styles.removeLink} onClick={() => handleConfirmReRecord(r.id)}>Record again</button>
                    <button type="button" className={styles.keepLink} onClick={handleCancelReRecord}>Keep it</button>
                  </span>
                ) : (
                  <div className={styles.recCardActions}>
                    {canReRecord && (
                      <button
                        type="button"
                        className={styles.removeLink}
                        onClick={() => handleStartReRecord(r.id)}
                        aria-label={kind === 'voice' ? 'Re-record this voice note' : 'Re-record this video'}
                      >
                        Re-record
                      </button>
                    )}
                    {!hideRemove && (
                      <button
                        type="button"
                        className={styles.removeLink}
                        onClick={() => setConfirmRemoveId(r.id)}
                        aria-label={kind === 'voice' ? 'Remove this voice note from this memory' : 'Remove this video from this memory'}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Idle: consent-first cues (R4; copy v0.2 §4) + record button (R2 cue).
          NEW-130: also reachable at the piece cap while replacingId is set —
          a replace doesn't grow the count, so it must stay reachable there. */}
      {activeRec.recordingState === 'idle' && (!atCap || replacingId != null) && (
        <div className={styles.recIdle}>
          {recordings.length === 0 && (
            <p className={styles.consentLine}>
              {kind === 'voice'
                ? "Before you record, you'll be asked to give access to your microphone"
                : "Before you record, you'll be asked to give access to your camera and microphone. Your camera stays off until you start recording"}
            </p>
          )}
          <button
            type="button"
            className={styles.recordBtn}
            onClick={() => activeRec.startRecording()}
            disabled={saving}
          >
            {kind === 'voice' ? <MicIcon /> : <CamIcon />}
            {kind === 'voice' ? 'Record a voice note' : 'Record a video'}
          </button>
          <button
            type="button"
            className={styles.libraryLink}
            onClick={() => (kind === 'video' ? videoFileInputRef : audioFileInputRef).current?.click()}
            disabled={saving}
          >
            Choose from your library
          </button>
          {libraryError && (
            <p className={styles.permissionLine} role="alert">{libraryError}</p>
          )}
          {activeRec.error && (
            <p className={styles.permissionLine} role="alert">{permissionLine(activeRec.error)}</p>
          )}
        </div>
      )}

      {/* Recording / paused (R6: timer + pulsing dot + word) */}
      {(activeRec.recordingState === 'recording' || activeRec.recordingState === 'paused') && (
        <div className={styles.recActive}>
          {kind === 'video' && (
            <video
              ref={videoPreviewRef}
              className={styles.videoPreview}
              autoPlay
              muted
              playsInline
            />
          )}
          {kind === 'voice' && (
            <span className={`${styles.waveform} ${styles.waveformLive}`} aria-hidden="true">
              {WAVE_HEIGHTS.map((h, i) => (
                <i key={i} style={{ height: `${h}px` }} />
              ))}
            </span>
          )}
          <div className={styles.recStateRow}>
            <span
              className={`${styles.recDot} ${activeRec.recordingState === 'paused' ? styles.recDotPaused : ''}`}
              aria-hidden="true"
            />
            <span className={styles.recStateWord}>
              {activeRec.recordingState === 'recording' ? 'Recording' : 'Paused'}
            </span>
            <span className={styles.recTimer}>{activeRec.durationFormatted}</span>
          </div>
          <div className={styles.recControls}>
            {activeRec.recordingState === 'recording' ? (
              <button type="button" className={styles.recCtl} onClick={() => activeRec.pauseRecording()}>Pause</button>
            ) : (
              <button type="button" className={styles.recCtl} onClick={() => activeRec.resumeRecording()}>Resume</button>
            )}
            <button type="button" className={`${styles.recCtl} ${styles.recCtlPrimary}`} onClick={() => activeRec.stopRecording()}>
              Stop
            </button>
          </div>
        </div>
      )}
      </RegionCard>

      {/* Hidden audio library picker input (voicemail case) */}
      <input
        ref={audioFileInputRef}
        type="file"
        accept={ACCEPTED_AUDIO_EXTENSIONS}
        className={styles.fileInput}
        onChange={handleAudioFileSelected}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Hidden library picker input (VideoPage v1.4 parity) */}
      <input
        ref={videoFileInputRef}
        type="file"
        accept={ACCEPTED_VIDEO_EXTENSIONS}
        className={styles.fileInput}
        onChange={handleVideoFileSelected}
        aria-hidden="true"
        tabIndex={-1}
      />
    </fieldset>
  );
}

/* ─── Static waveform bar heights (AD §1.10 — decorative, aria-hidden) — used
   only for the LIVE in-progress recording animation now; per-piece preview
   waveforms come from VoiceCard's own real/fallback rendering. ─── */
const WAVE_HEIGHTS = [8, 14, 22, 12, 26, 18, 10, 24, 16, 28, 12, 20, 8, 18, 26, 14, 22, 10, 16, 12];

/* ─── Icons (stroke style matches LeaveConfirmModal SVGs) ─── */

function MicIcon() {
  return (
    <svg className={styles.segIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );
}

function CamIcon() {
  return (
    <svg className={styles.segIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}
